import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useGlobalNotifications, type Notification } from '../contexts/NotificationContext';
import { cn } from '../utils/cn';

interface NotificationToastProps {
  notification: Notification;
  onRemove: (id: string) => void;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  notification,
  onRemove,
}) => {
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-600" />;
      default:
        return <Info className="h-5 w-5 text-gray-600" />;
    }
  };

  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div
      className={cn(
        'flex items-start space-x-3 p-4 rounded-lg border shadow-lg max-w-sm w-full animate-in slide-in-from-right-full duration-300',
        getBackgroundColor()
      )}
    >
      <div className="flex-shrink-0" aria-hidden="true">{getIcon()}</div>

      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900">
          {notification.title}
        </h4>
        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
      </div>

      <button
        onClick={() => onRemove(notification.id)}
        aria-label="Cerrar aviso"
        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};

/**
 * GH#1802 — ningún aviso se anunciaba a lector de pantalla (WCAG 4.1.3): el
 * contenedor no tenía región viva y encima solo existía mientras había un aviso
 * (una región viva tiene que estar en el DOM ANTES de cambiar, o no se anuncia).
 *
 * Ahora el contenedor se monta SIEMPRE, con dos regiones vivas permanentes:
 * - role="status" / aria-live="polite" para éxito e info (no interrumpe).
 * - role="alert" / aria-live="assertive" para error y advertencia (urgente).
 * El auto-descarte de errores/avisos se alargó a ≥10s en el contexto (WCAG 2.2.1).
 */
const GlobalNotificationContainer: React.FC = () => {
  const { notifications, removeNotification } = useGlobalNotifications();

  const corteses = notifications.filter(
    (n) => n.type === 'success' || n.type === 'info',
  );
  const urgentes = notifications.filter(
    (n) => n.type === 'error' || n.type === 'warning',
  );

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {/* Éxito / info: no interrumpe la lectura en curso. */}
      <div role="status" aria-live="polite" className="flex flex-col gap-2 [&>*]:pointer-events-auto">
        {corteses.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onRemove={removeNotification}
          />
        ))}
      </div>
      {/* Error / advertencia: se anuncia de inmediato. */}
      <div role="alert" aria-live="assertive" className="flex flex-col gap-2 [&>*]:pointer-events-auto">
        {urgentes.map((notification) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onRemove={removeNotification}
          />
        ))}
      </div>
    </div>
  );
};

export default GlobalNotificationContainer;
